import type { AdminRunTrace, AdminRunTraceSpan } from '@/api/adminClient';

export type TraceRow = {
  span: AdminRunTraceSpan;
  depth: number;
  hasChildren: boolean;
  /** Where the bar starts and how wide it is, as percentages of the trace. */
  leftPct: number;
  widthPct: number;
  /** Duration shown for the bar; an open span runs to `nowMs`. */
  durationMs: number;
};

/** The trace's extent: its terminal offset, else the furthest span end, else now for a run in flight. */
export function traceExtentMs(trace: AdminRunTrace, nowMs: number): number {
  const ends = trace.spans.map((span) => span.endMs ?? (trace.inProgress ? nowMs : span.startMs));
  return Math.max(1, trace.endMs ?? 0, ...ends, trace.inProgress ? nowMs : 0);
}

/**
 * Depth-first rows (children under their parent, each level in start order) with bar
 * geometry on one scale. Children of a collapsed span are left out.
 */
export function layoutTrace(trace: AdminRunTrace, nowMs: number, collapsed: ReadonlySet<string> = new Set()): TraceRow[] {
  const extent = traceExtentMs(trace, nowMs);
  const ids = new Set(trace.spans.map((span) => span.id));
  const children = new Map<string | undefined, AdminRunTraceSpan[]>();
  for (const span of trace.spans) {
    const parent = span.parentId && ids.has(span.parentId) ? span.parentId : undefined;
    const list = children.get(parent) ?? [];
    list.push(span);
    children.set(parent, list);
  }
  for (const list of children.values()) list.sort((a, b) => a.startMs - b.startMs);
  const rows: TraceRow[] = [];
  const visit = (parent: string | undefined, depth: number) => {
    for (const span of children.get(parent) ?? []) {
      const end = span.endMs ?? Math.max(span.startMs, nowMs);
      const hasChildren = (children.get(span.id)?.length ?? 0) > 0;
      rows.push({
        span, depth, hasChildren,
        leftPct: (span.startMs / extent) * 100,
        widthPct: Math.max(0, ((end - span.startMs) / extent) * 100),
        durationMs: Math.max(0, end - span.startMs),
      });
      if (hasChildren && !collapsed.has(span.id)) visit(span.id, depth + 1);
    }
  };
  visit(undefined, 0);
  return rows;
}

/** Round axis ticks for a trace of `extentMs`, about five of them. */
export function traceTicks(extentMs: number): number[] {
  const steps = [100, 250, 500, 1_000, 2_000, 5_000, 10_000, 15_000, 30_000, 60_000, 120_000, 300_000, 600_000, 1_800_000, 3_600_000];
  const step = steps.find((candidate) => extentMs / candidate <= 6) ?? steps[steps.length - 1];
  const ticks: number[] = [];
  for (let at = 0; at <= extentMs; at += step) ticks.push(at);
  return ticks;
}

export function formatTick(ms: number): string {
  if (ms === 0) return '0';
  if (ms < 1_000) return `${ms}ms`;
  if (ms < 60_000) return `${ms / 1_000}s`;
  return `${Math.round(ms / 60_000)}m`;
}

/** The share of the trace each top-level span took, for the one-line summary. */
export function topLevelShare(trace: AdminRunTrace, nowMs: number): Array<{ id: string; label: string; durationMs: number; pct: number }> {
  const extent = traceExtentMs(trace, nowMs);
  return trace.spans
    .filter((span) => !span.parentId && span.id !== 'failed')
    .map((span) => {
      const durationMs = Math.max(0, (span.endMs ?? nowMs) - span.startMs);
      return { id: span.id, label: span.label, durationMs, pct: (durationMs / extent) * 100 };
    });
}
